import { create } from 'zustand';
import { listsApi, integrationApi } from '../lib/api';
import type { ListEntityKind, FilterSpec, SortSpec } from '../types/list';
import { defaultVisible } from '../lib/exploreColumns';

interface Pagination { page: number; limit: number; totalCount: number; totalPages: number; hasNextPage: boolean; hasPrevPage: boolean; }

interface ExploreState {
  kind: ListEntityKind;            // 'ORG' | 'PROGRAM' | 'PERSON'
  filterSpec: FilterSpec;
  sort?: SortSpec;
  page: number;
  rows: any[];
  pagination: Pagination | null;
  isLoading: boolean;
  error: string | null;
  selected: Map<string, ListEntityKind>;  // id -> real kind
  visibleColumns: string[];
  sportOptions: { value: string; label: string }[];
  setKind: (k: ListEntityKind) => void;
  setKindAndFilter: (kind: ListEntityKind, filterSpec: FilterSpec) => void;
  setFilter: (patch: Partial<FilterSpec>) => void;
  clearFilter: () => void;
  setSort: (s?: SortSpec) => void;
  setPage: (p: number) => void;
  setVisibleColumns: (keys: string[]) => void;
  toggleSelect: (id: string, kind: ListEntityKind) => void;
  selectAllOnPage: (rows: { id: string; kind: ListEntityKind }[]) => void;
  resetSelection: () => void;
  fetchPreview: () => Promise<void>;
}

const LS = (k: ListEntityKind) => `explore.cols.${k}`;
function loadCols(k: ListEntityKind): string[] {
  try { const raw = localStorage.getItem(LS(k)); if (raw) return JSON.parse(raw); } catch { /* ignore */ }
  return defaultVisible(k);
}

export const useExploreStore = create<ExploreState>((set, get) => ({
  kind: 'ORG',
  filterSpec: { version: 1 },
  sort: undefined,
  page: 1,
  rows: [],
  pagination: null,
  isLoading: false,
  error: null,
  selected: new Map(),
  visibleColumns: loadCols('ORG'),
  sportOptions: [],
  // Clear rows/pagination on a kind switch: the grid's row renderers are kind-specific
  // (row.person / row.team / row.organization), so rendering a previous tab's rows under
  // the new kind would throw. isLoading:true keeps the grid on its loading state until the
  // matching rows arrive.
  setKind: (kind) => { set({ kind, filterSpec: { version: 1 }, sort: undefined, page: 1, selected: new Map(), visibleColumns: loadCols(kind), rows: [], pagination: null, isLoading: true, error: null }); get().fetchPreview(); },
  setKindAndFilter: (kind, filterSpec) => { set({ kind, filterSpec, sort: undefined, page: 1, selected: new Map(), visibleColumns: loadCols(kind), rows: [], pagination: null, isLoading: true, error: null }); get().fetchPreview(); },
  setFilter: (patch) => { set({ filterSpec: { ...get().filterSpec, ...patch }, page: 1 }); get().fetchPreview(); },
  clearFilter: () => { set({ filterSpec: { version: 1 }, page: 1 }); get().fetchPreview(); },
  setSort: (sort) => { set({ sort, page: 1 }); get().fetchPreview(); },
  setPage: (page) => { set({ page }); get().fetchPreview(); },
  setVisibleColumns: (keys) => { try { localStorage.setItem(LS(get().kind), JSON.stringify(keys)); } catch { /* ignore */ } set({ visibleColumns: keys }); },
  toggleSelect: (id, kind) => { const m = new Map(get().selected); if (m.has(id)) m.delete(id); else m.set(id, kind); set({ selected: m }); },
  selectAllOnPage: (rows) => { const m = new Map(get().selected); const allSel = rows.every((r) => m.has(r.id)); rows.forEach((r) => allSel ? m.delete(r.id) : m.set(r.id, r.kind)); set({ selected: m }); },
  resetSelection: () => set({ selected: new Map() }),
  fetchPreview: async () => {
    const { kind, filterSpec, sort, page } = get();
    set({ isLoading: true, error: null });
    try {
      const data = await listsApi.preview(kind, filterSpec, { page, limit: 25, sort });
      set({ rows: data.items, pagination: data.pagination, isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load', isLoading: false });
    }
  },
}));

// Fire-and-forget: fetch all Blast sports once and cache them in the store for the Programs Sport dropdown.
integrationApi.getBlastSports().then((sports) => {
  const seen = new Set<string>();
  const sportOptions = sports
    .map((s) => ({ value: s.name, label: s.name }))
    .filter((o) => { if (seen.has(o.value)) return false; seen.add(o.value); return true; })
    .sort((a, b) => a.label.localeCompare(b.label));
  useExploreStore.setState({ sportOptions });
}).catch(() => { /* leave sportOptions as [] on failure */ });

// Writes (kept here so the page stays thin; call listsApi directly to avoid listStore side-effects)
export async function addSelectedToList(listId: string, selected: Map<string, ListEntityKind>): Promise<number> {
  const items = Array.from(selected.entries()).map(([id, kind]) => ({ kind, id }));
  const { added } = await listsApi.addItems(listId, items);
  return added;
}
export async function addAllMatchingToList(listId: string, kind: ListEntityKind, filterSpec: FilterSpec): Promise<number> {
  const { added } = await listsApi.addFromFilter(listId, kind, filterSpec);
  return added;
}
export async function saveSmartList(name: string, kind: ListEntityKind, filterSpec: FilterSpec) {
  return listsApi.create({ name, kind: 'SMART', smartEntityKind: kind, filterSpec });
}
export async function updateSmartFilter(listId: string, filterSpec: FilterSpec) {
  return listsApi.update(listId, { filterSpec });
}
